import { ReactElement, Suspense, useEffect, useState } from 'react';
import {
  Button,
  TableColumns,
  TableContainer,
  TableRows,
  useTableContext,
  ContextualTablePagination,
  FullLoader,
  Pill,
  IconButton,
} from '@sc/shared-ui';
import {
  CalendarIcon,
  GlobeAltIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { Reference } from '@apollo/client';
import { useMutation, useSuspenseQuery } from '@apollo/client/react';
import {
  GQL_MUTATION_DELETE_DOMAIN,
  GQL_QUERY_INDEX_DOMAINS,
} from '../utility/queries';
import Globe from '../assets/icons/globe-01.svg';
import { useAlerts } from '../providers/alerts';
import { notify } from '../utility/notify';
import { format } from 'date-fns';
import assertsSubscription from '../utility/assertsSubscription';
import { useUser } from '../providers/users';
import { SubscriptionType, UserCompanyLimitation } from '@sc/graphql';
import classNames from 'classnames';
import { CreateDomainModal } from '../modals/CreateDomainModal';
import {
  DomainVerificationModal,
  VerifiableDomain,
} from '../modals/DomainVerificationModal';
import { track } from '../utility/analytics';

const DomainTableRows = ({
  onViewInstructions,
}: {
  onViewInstructions: (domain: VerifiableDomain) => void;
}) => {
  const { setTotalPages, page, rowsPerPage } = useTableContext();

  const { setAlertModal, useApolloError } = useAlerts();

  const onError = useApolloError();

  const { data } = useSuspenseQuery(GQL_QUERY_INDEX_DOMAINS, {
    variables: {
      input: {
        pagination: {
          page,
          perPage: rowsPerPage,
        },
        filters: undefined,
      },
    },
    context: {
      isBatched: true,
    },
  });

  useEffect(() => {
    setTotalPages(data.indexDomains.pagination.lastPage);
  }, [data, setTotalPages]);

  const [deleteDomain] = useMutation(GQL_MUTATION_DELETE_DOMAIN);

  return (
    <TableRows
      widthType="pc"
      rows={data.indexDomains.items.map((p) => ({
        uuid: p.uuid,
        cells: [
          {
            width: 20,
            content: (
              <div className="flex items-center space-x-2">
                <Globe className="size-5 text-grey-400" />
                <span className="text-sm">{p.url}</span>
              </div>
            ),
          },
          {
            width: 20,
            content: (
              <div className="flex items-center space-x-2">
                <CalendarIcon className="size-5 text-grey-400" />
                <span className="text-sm">
                  {format(p.createdAt, 'd MMM yyyy')}
                </span>
              </div>
            ),
          },
          {
            width: 20,
            content: (
              <Pill
                size={'small'}
                text={p.cloudflareStatus === 'active' ? 'Active' : 'Pending'}
                colour={p.cloudflareStatus === 'active' ? '#3EB077' : '#DA892B'}
              />
            ),
          },
          {
            width: 20,
            // leaving this in for when we allow embed via widget, needs to be fetched from the API
            content: p.projectUuid ? 1 : 0,
          },
          {
            width: 20,
            wrapperClassname: 'justify-end',
            content: (
              <div className="space-x-2 flex items-center">
                <Button
                  button={{
                    text: 'View instructions',
                    type: 'low-priority',
                    size: 'small',
                    shrink: true,
                    onClick: () =>
                      onViewInstructions({ uuid: p.uuid, url: p.url }),
                  }}
                />
                <IconButton
                  Icon={TrashIcon}
                  type="tertiary"
                  size="small"
                  onClick={() =>
                    setAlertModal({
                      open: true,
                      title: `Delete ${p.url}`,
                      description: `Are you sure you want to delete this domain name? You will no longer be able to access directly any associated knowledge bases.`,
                      icon: 'critical',
                      isDelete: true,
                      submitText: 'Confirm deletion',
                      onSubmit: () => {
                        setAlertModal(undefined);

                        void deleteDomain({
                          variables: {
                            input: {
                              uuid: p.uuid,
                            },
                          },
                          optimisticResponse: {
                            deleteDomain: true,
                          },
                          update: (cache) => {
                            cache.modify<{
                              indexDomains: { items: Reference[] };
                            }>({
                              fields: {
                                indexDomains: (existing, { readField }) =>
                                  'items' in existing
                                    ? {
                                        ...existing,
                                        items: existing.items.filter(
                                          (item) =>
                                            readField('uuid', item) !== p.uuid
                                        ),
                                      }
                                    : existing,
                              },
                            });
                          },
                          refetchQueries: [GQL_QUERY_INDEX_DOMAINS],
                          onCompleted: () => {
                            track('Domain Deleted', { domainUuid: p.uuid });

                            notify.success('Successfully deleted domain');
                          },
                          onError,
                        });
                      },
                    })
                  }
                />
              </div>
            ),
          },
        ],
      }))}
    />
  );
};

const DomainsTable = assertsSubscription(
  ({
    onViewInstructions,
  }: {
    onViewInstructions: (domain: VerifiableDomain) => void;
  }) => (
    <TableContainer>
      <TableColumns
        widthType="pc"
        columns={[
          {
            width: 20,
            heading: 'Domain',
          },
          {
            width: 20,
            heading: 'Added',
          },
          {
            width: 20,
            heading: 'State',
          },
          {
            width: 20,
            heading: 'Knowledge bases',
          },
          {
            width: 20,
          },
        ]}
      />

      <Suspense fallback={<FullLoader className="size-6" />}>
        <DomainTableRows onViewInstructions={onViewInstructions} />
      </Suspense>
      <ContextualTablePagination />
    </TableContainer>
  ),
  {
    upgradePrompt: 'Unlock custom domains to give branded access to your team.',
    subscriptionType: [
      SubscriptionType.essentials,
      SubscriptionType.enterprise,
    ],
    Icon: GlobeAltIcon,
  }
);

export const Domains = (): ReactElement => {
  const [openCreateDomainModal, setOpenCreateDomainModal] = useState(false);

  const [verifyDomain, setVerifyDomain] = useState<
    VerifiableDomain | undefined
  >(undefined);

  const { user } = useUser();

  const userCannotAddCustomDomain = user.company.limitations.includes(
    UserCompanyLimitation.cannotCreateDomain
  );

  return (
    <>
      <div className="p-5 flex items-center justify-between">
        <h1 className="text-h1-small font-semibold font-grotesque">Domains</h1>
        <Button
          button={{
            text: 'Create domain',
            size: 'medium',
            shrink: true,
            disabled: userCannotAddCustomDomain,
            IconEnd: PlusIcon,
            onClick: () => {
              track('Create Domain Modal Viewed');

              setOpenCreateDomainModal(true);
            },
          }}
        />
      </div>
      <div
        className={classNames('pb-5 flex flex-col', {
          'px-5': userCannotAddCustomDomain,
        })}
      >
        <DomainsTable onViewInstructions={setVerifyDomain} />
      </div>

      <CreateDomainModal
        open={openCreateDomainModal}
        onClose={() => setOpenCreateDomainModal(false)}
        onCreated={setVerifyDomain}
      />
      <DomainVerificationModal
        domain={verifyDomain}
        onClose={() => setVerifyDomain(undefined)}
      />
    </>
  );
};
